import { Film, Loader2, Search } from "lucide-react";
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "../../../shared/components/ui/drawer";
import { SPACING } from "../../../shared/constants/spacing";
import type { SearchResult } from "../hooks/useSearch";

interface MobileSearchDrawerProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly inputValue: string;
  readonly onInputChange: (value: string) => void;
  readonly onKeyDown: (e: React.KeyboardEvent) => void;
  readonly loading: boolean;
  readonly results: SearchResult[];
  readonly recentSearches: string[];
  readonly onResultSelect: (result: SearchResult) => void;
  readonly onRecentSearchSelect: (query: string) => void;
  readonly onViewAllResults: () => void;
}

/**
 * Mobile search result item - simplified version without Command context
 */
function MobileSearchResultItem({
  result,
  onSelect,
}: {
  readonly result: SearchResult;
  readonly onSelect: () => void;
}) {
  const posterUrl = result.verticalTileUrl || result.posterUrlHd;
  const year = result.year ?? "";
  const runtime = result.runtimeMinutes
    ? `${Math.floor(result.runtimeMinutes / 60)}h ${result.runtimeMinutes % 60}m`
    : null;

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    img.style.display = "none";
    if (img.nextElementSibling) {
      (img.nextElementSibling as HTMLElement).style.display = "flex";
    }
  };

  return (
    <button
      onClick={onSelect}
      className="w-full flex items-start gap-4 px-4 py-4 hover:bg-neutral-800/50 rounded-lg transition-colors text-left"
    >
      <div className="w-16 h-24 flex-shrink-0 relative">
        {posterUrl ? (
          <>
            <img
              src={posterUrl}
              alt={result.name}
              onError={handleImageError}
              className="w-full h-full object-cover rounded"
            />
            <div
              className="w-full h-full bg-neutral-800 rounded flex items-center justify-center absolute inset-0"
              style={{ display: "none" }}
            >
              <Film className="w-8 h-8 text-neutral-600" />
            </div>
          </>
        ) : (
          <div className="w-full h-full bg-neutral-800 rounded flex items-center justify-center">
            <Film className="w-8 h-8 text-neutral-600" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-lg font-semibold text-white mb-1">{result.name}</div>
        <div className="text-xs text-neutral-400 space-y-0.5">
          <div className="truncate">
            {result.directorName && <span>{result.directorName}</span>}
            {result.directorName && (year || result.country) && <span> • </span>}
            {year && <span>{year}</span>}
            {year && result.country && <span> • </span>}
            {result.country && <span>{result.country}</span>}
          </div>
          {(result.genres.length > 0 || result.languages.length > 0) && (
            <div className="truncate">
              {result.genres.length > 0 && <span>{result.genres.join(", ")}</span>}
              {result.genres.length > 0 && result.languages.length > 0 && <span> • </span>}
              {result.languages.length > 0 && <span>{result.languages.join(", ")}</span>}
            </div>
          )}
          {runtime && <div>{runtime}</div>}
        </div>
      </div>
    </button>
  );
}

interface MobileSearchDrawerProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly inputValue: string;
  readonly onInputChange: (value: string) => void;
  readonly onKeyDown: (e: React.KeyboardEvent) => void;
  readonly loading: boolean;
  readonly results: SearchResult[];
  readonly recentSearches: string[];
  readonly onResultSelect: (result: SearchResult) => void;
  readonly onRecentSearchSelect: (query: string) => void;
  readonly onViewAllResults: () => void;
}

/**
 * Mobile search drawer - full screen slide-up interface
 */
export function MobileSearchDrawer({
  open,
  onOpenChange,
  inputValue,
  onInputChange,
  onKeyDown,
  loading,
  results,
  recentSearches,
  onResultSelect,
  onRecentSearchSelect,
  onViewAllResults,
}: MobileSearchDrawerProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-black border-neutral-800 max-h-[90vh]">
        {/* Hidden accessibility labels */}
        <DrawerTitle className="sr-only">Search</DrawerTitle>
        <DrawerDescription className="sr-only">Search for movies and TV shows</DrawerDescription>

        <div className="p-4 flex flex-col gap-4 overflow-hidden">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <input
              type="text"
              value={inputValue}
              onChange={(e) => onInputChange(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search titles..."
              className={`w-full pl-10 pr-4 ${SPACING.search.inputPadding} bg-neutral-900 border border-neutral-800 rounded-lg text-white placeholder-neutral-500 focus:outline-none focus:border-lumi-accent`}
              autoFocus
            />
          </div>

          <div className="flex-1 overflow-y-auto space-y-4">
            {!inputValue && recentSearches.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-neutral-400 mb-2 px-2">Recent Searches</h3>
                <div className="space-y-1">
                  {recentSearches.map((query) => (
                    <button
                      key={query}
                      onClick={() => onRecentSearchSelect(query)}
                      className={`w-full text-left px-4 ${SPACING.search.inputPadding} hover:bg-neutral-900 rounded-lg text-white transition-colors`}
                    >
                      {query}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-lumi-accent" />
              </div>
            )}

            {!loading && inputValue && results.length === 0 && (
              <div className="text-center py-8 text-neutral-500">No results found for "{inputValue}"</div>
            )}

            {!loading && results.length > 0 && (
              <div className="space-y-1">
                {results.map((result) => (
                  <MobileSearchResultItem key={result.id} result={result} onSelect={() => onResultSelect(result)} />
                ))}
                <button
                  onClick={onViewAllResults}
                  className={`w-full px-4 ${SPACING.search.inputPadding} text-center text-lumi-accent hover:bg-neutral-900 rounded-lg transition-colors font-medium`}
                >
                  View All Results
                </button>
              </div>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
